import { Box } from "@mui/material";
import DeleteMenuOption from "components/TableMonitoring/Body/RowMenuListActions/DeleteMenuOption";
import TableHeaderStatic from "components/TableMonitoring/Header/Static/TableHeaderStatic";
import TableMonitoring from "components/TableMonitoring";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PatientContentTableRows from "./PatientContentTableRows";
import TableRowStatic from "components/TableMonitoring/Body/TableRowStatic";
import { initializePatientContent } from "redux/reducers/patientContentReducer";
import DisplayEntity from "components/PageBuilder/DisplayEntity";
import {
  CommonTexts,
  ContentFormsLabels,
  patientContentTexts,
} from "locale/es";
import Loader from "components/Loader/Loader";
import NoDataTableBody from "components/TableMonitoring/Body/NoDataTableBody";
import { Outlet } from "react-router-dom";
import { selectPatient } from "redux/reducers/selectedPatientReducer";
import { SearchBar } from "components/SearchPatients/SearchBar";
import { useFilterContext } from "context/FilterContext";
import { useFilter } from "hooks/useFilter";
import FilterTable from "components/TableMonitoring/Toolbar/Filter";
import { contentTexts } from "locale/es";

const tableHeaders = [
  {
    label: ContentFormsLabels.START_DATE,
    name: ContentFormsLabels.START_DATE,
    value: "startDate",
    id: "startDate",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.TITLE,
    name: ContentFormsLabels.TITLE,
    value: "title",
    id: "title",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.END_DATE,
    name: ContentFormsLabels.END_DATE,
    value: "endDate",
    id: "endDate",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.SCORE,
    name: ContentFormsLabels.SCORE,
    value: "score",
    id: "score",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.VISITED,
    name: ContentFormsLabels.VISITED,
    value: "visited",
    id: "visited",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.FIRST_VISITED,
    name: ContentFormsLabels.FIRST_VISITED,
    value: "firstVisitDate",
    id: "firstVisitDate",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.VISITED_LINK,
    name: ContentFormsLabels.VISITED_LINK,
    value: "linkVisited",
    id: "linkVisited",
    isSortable: false,
  },
  {
    label: ContentFormsLabels.ENABLED,
    name: ContentFormsLabels.ENABLED,
    value: "enabled",
    id: "enabled",
    isSortable: false,
  },
];

function PatientContent({ isDashboard, isDashboardView }) {
  const patient = useSelector(({ patient }) => patient);
  const { selectedFilter, searchTerm } = useFilterContext();

  const { data, loading, error } = useSelector(
    ({ patientContent }) => patientContent
  );

  const adaptedData = useMemo(() => data.map(item => ({
    ...item,
    title: item.content?.title,
    link: item.content?.link
  })), [data]);

  const allPatients = useSelector(({ allPatients }) => allPatients.data);
  const selectedPatient = useSelector(({ patient }) => patient);

  const dispatch = useDispatch();

  const adaptedTableHeaders = tableHeaders.map((header) => header.name);

  const { filteredData } = useFilter(adaptedData, adaptedTableHeaders);

  const handleChangeSelectedPatient = (event, value) => {
    if (value) {
      dispatch(selectPatient(value));
    }
  };

  const findSelectedPatient = () =>
    allPatients.find((patient) => patient.id === selectedPatient.id);

  useEffect(() => {
    if (patient.user?.id && data?.length === 0) {
      dispatch(initializePatientContent(patient.id));
    }
  }, [patient.user?.id, dispatch]);

  if (loading) {
    return <Loader message={patientContentTexts.LOADING} pt={32} />;
  }

  return (
    <>
      {!isDashboard && (
        <Box sx={{ width: 350, mt: 2, mb: -2 }}>
          <SearchBar
            allDataSearch={allPatients}
            selectedData={selectedPatient}
            isPatientComponent={true}
            handleChange={handleChangeSelectedPatient}
            findSelected={findSelectedPatient() || ""}
            placeholder={CommonTexts.SEARCH_PATIENT}
          />
        </Box>
      )}
      <Box
        sx={
          !isDashboard
            ? { flex: 1, p: 3, mb: 20, minWidth: "100%" }
            : { mb: 20, mt: -4, minWidth: "100%" }
        }
      >
        <DisplayEntity entityId={patient.user?.id}>
          <FilterTable data={data} tableHeaders={tableHeaders} />
          <TableMonitoring
            tableHeader={
              <TableHeaderStatic tableHeaders={adaptedTableHeaders} />
            }
          >
            {(selectedFilter && !searchTerm ? data : filteredData).map(
              (content) => (
                <TableRowStatic
                  key={content.id}
                  entity={content}
                  loaded={!loading}
                  actions={<DeleteMenuOption entityId={content.id} />}
                >
                  <PatientContentTableRows content={content} isDashboardView={isDashboardView}/>
                </TableRowStatic>
              )
            )}
          </TableMonitoring>
          {data?.length === 0 && (
            <NoDataTableBody
              entity={patient.id}
              noDataMessage={patientContentTexts.NO_DATA}
            />
          )}
          {data?.length > 0 && filteredData?.length === 0 && searchTerm && (
            <NoDataTableBody
              entity={patient.id}
              noDataMessage={contentTexts.NO_DATA_FILTERS}
            />
          )}
        </DisplayEntity>
      </Box>
      <Outlet />
    </>
  );
}

export default PatientContent;
